import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEnvelope, FaUser, FaUserGraduate, FaChalkboardTeacher, FaSpinner, FaGraduationCap } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { registerUser } from '../../services/authService';
import SEO from '../../components/common/SEO';
import PasswordInput from '../../components/common/PasswordInput';
import PageSection from '../../components/common/PageSection';

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'student' });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.error('Please fill in all fields');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      const { token, user } = await registerUser(form);
      login(token, user);
      toast.success(`Account created! Welcome, ${user.name}!`);

      if (user.role === 'teacher') {
        navigate('/dashboard/teacher', { replace: true });
      } else {
        navigate('/dashboard/student', { replace: true });
      }
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Create Account | Arvix Creative"
        description="Create your Arvix Creative student or teacher account"
      />
      <PageSection className="pt-24 sm:pt-28 md:pt-32 pb-12 md:pb-16">
        <div className="flex items-center justify-center min-h-[70vh] px-4">
          <div className="w-full max-w-md">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-red-100/50 shadow-lg p-6 sm:p-8 md:p-10">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-100 text-designer-primary text-xs font-bold tracking-widest mb-5">
                  <FaGraduationCap className="w-3.5 h-3.5" />
                  ARVIX ACADEMY
                </div>
                <h1 className="text-2xl font-bold text-designer-dark">Create Your Account</h1>
                <p className="text-gray-500 text-sm mt-1">Choose your role and join the Academy</p>
              </div>

              {/* Role Selector */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, role: 'student' }))}
                  className={`flex flex-col items-center gap-2 py-4 px-3 rounded-xl border-2 transition-all duration-200 ${
                    form.role === 'student'
                      ? 'border-designer-primary bg-red-50 text-designer-primary'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <FaUserGraduate className="w-5 h-5" />
                  <span className="text-sm font-semibold">Student</span>
                  <span className="text-xs text-center opacity-70">Enroll in courses</span>
                </button>
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, role: 'teacher' }))}
                  className={`flex flex-col items-center gap-2 py-4 px-3 rounded-xl border-2 transition-all duration-200 ${
                    form.role === 'teacher'
                      ? 'border-designer-primary bg-red-50 text-designer-primary'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <FaChalkboardTeacher className="w-5 h-5" />
                  <span className="text-sm font-semibold">Teacher</span>
                  <span className="text-xs text-center opacity-70">Create courses</span>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Full Name
                  </label>
                  <div className="relative">
                    <FaUser className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-designer-primary/30 focus:border-designer-primary transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-designer-primary/30 focus:border-designer-primary transition-all duration-200"
                    />
                  </div>
                </div>

                <PasswordInput
                  id="password"
                  name="password"
                  label="Password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Min 6 characters"
                />

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-gradient-to-r from-designer-primary to-red-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-red-500/30 hover:scale-[1.02] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
                >
                  {isLoading ? (
                    <>
                      <FaSpinner className="animate-spin w-4 h-4" />
                      Creating account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </form>

              <p className="text-center text-sm text-gray-500 mt-6">
                Already have an account?{' '}
                <Link to="/login" className="text-designer-primary font-semibold hover:underline">
                  Sign in
                </Link>
              </p>
              <p className="text-center text-xs text-gray-400 mt-3">
                <Link to="/academy" className="hover:text-designer-primary transition-colors">
                  ← Back to Arvix Academy
                </Link>
              </p>
            </div>
          </div>
        </div>
      </PageSection>
    </>
  );
};

export default Register;
